import * as React from "react";
import { CheckboxIcon } from "@salt-ds/core";
import { CheckmarkIcon, CheckmarkSolidIcon } from "@salt-ds/icons";

import { useFormFieldProps } from "../salt/form-field-context";
import { useControlled } from "./useControlled";
import "./toggles.css";

/**
 * Checkbox and Switch, replacing `@salt-ds/core`'s for 50 and 49 elements.
 *
 * ── BOTH ARE A <label> WRAPPING A REAL <input type="checkbox"> ────────────────────────
 *
 * The visible box or track is a sibling `<span>` that the CSS draws; the real control is a
 * native checkbox, visually hidden but present. That is what buys keyboard operation, form
 * participation and screen-reader semantics for free. Switch adds `role="switch"` so it is
 * announced as on/off rather than checked/unchecked.
 *
 * ── readOnly IS ENFORCED IN THE HANDLER, NOT JUST ANNOUNCED ───────────────────────────
 *
 * A native checkbox ignores the `readonly` attribute entirely — unlike a text input, it stays
 * fully clickable. So read-only has to be implemented by refusing the change, and communicated
 * with `aria-readonly`. Dropping the guard leaves a control that announces itself as read-only
 * and then changes anyway.
 *
 * ── indeterminate HAS NO REACT ATTRIBUTE ──────────────────────────────────────────────
 *
 * It is an IDL property on the DOM node with no HTML attribute behind it, so React cannot set
 * it from JSX. It must be assigned imperatively in a layout effect, before paint, or the
 * partially-checked state renders as plain unchecked for a frame.
 *
 * ── SCOPE: NO CheckboxGroup ───────────────────────────────────────────────────────────
 *
 * The vendor's Checkbox also participates in a CheckboxGroup, which owns selection, disabled,
 * validation and labelling for its children. That is deliberately NOT ported: this app uses
 * CheckboxGroup in zero places, and the vendor does not export its context, so there is no way
 * to bridge it the way `FormFieldContext` is bridged. A Salt `CheckboxGroup` wrapped around one
 * of these would therefore not drive it. If that group is ever wanted, port the group too —
 * do not reach for a private context.
 */
function join(...values: (string | undefined)[]): string | undefined {
  return values.filter(Boolean).join(" ") || undefined;
}

export type ToggleValidationStatus = "error" | "warning" | "success";

/*
 * Both prop types extend `InputHTMLAttributes`, matching the vendor's declaration, even though
 * `...rest` is spread onto the <label>. It looks like a mistake and is not one: call sites pass
 * `form`, `required`, `tabIndex` and `id` here, and the vendor accepts them. Narrowing this to
 * label attributes broke four real files the moment the shadow landed.
 */
type DataAttributes = Record<`data-${string}`, unknown>;

export interface CheckboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLLabelElement>,
    "onChange" | "onBlur" | "onFocus" | "value" | "defaultChecked" | "checked"
  > {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  error?: boolean;
  indeterminate?: boolean;
  inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>> & DataAttributes;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: React.ReactNode;
  name?: string;
  readOnly?: boolean;
  validationStatus?: ToggleValidationStatus;
  value?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

export const Checkbox = React.forwardRef<HTMLLabelElement, CheckboxProps>(function Checkbox(
  {
    checked: checkedProp,
    className,
    defaultChecked,
    disabled: disabledProp,
    error,
    indeterminate,
    inputProps = {},
    inputRef: inputRefProp,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    readOnly: readOnlyProp,
    validationStatus: validationStatusProp,
    value,
    ...rest
  },
  ref,
) {
  const {
    "aria-describedby": inputDescribedBy,
    "aria-labelledby": inputLabelledBy,
    className: inputClassName,
    onChange: inputOnChange,
    ...restInputProps
  } = inputProps;

  const [checked, setChecked] = useControlled<boolean>({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
  });

  const {
    a11yProps: formFieldA11yProps,
    disabled: formFieldDisabled,
    readOnly: formFieldReadOnly,
    validationStatus: formFieldValidationStatus,
  } = useFormFieldProps();

  const disabled = formFieldDisabled || disabledProp;
  const readOnly = formFieldReadOnly || readOnlyProp;
  // Suppressed entirely when disabled: a validation colour on a control nobody can change is
  // decoration, and it competes with the disabled styling for the same pixels.
  const validationStatus = !disabled
    ? (formFieldValidationStatus ?? validationStatusProp)
    : undefined;

  const innerRef = React.useRef<HTMLInputElement | null>(null);
  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof inputRefProp === "function") inputRefProp(node);
      else if (inputRefProp)
        (inputRefProp as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [inputRefProp],
  );

  // Layout effect, not effect: this runs before paint, so the partially-checked state is never
  // shown as plain unchecked for a frame.
  React.useLayoutEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate ?? false;
  }, [indeterminate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.nativeEvent.defaultPrevented || readOnly) return;
    setChecked(event.target.checked);
    onChange?.(event);
    inputOnChange?.(event);
  };

  const classes = ["saltCheckbox"];
  if (disabled) classes.push("saltCheckbox-disabled");
  if (readOnly) classes.push("saltCheckbox-readOnly");
  if (error) classes.push("saltCheckbox-error");
  if (validationStatus) classes.push(`saltCheckbox-${validationStatus}`);
  if (className) classes.push(className);

  return (
    <label className={classes.join(" ")} ref={ref} {...rest}>
      <input
        aria-describedby={join(formFieldA11yProps?.["aria-describedby"], inputDescribedBy)}
        aria-labelledby={join(formFieldA11yProps?.["aria-labelledby"], inputLabelledBy)}
        aria-readonly={readOnly || undefined}
        checked={checked}
        className={inputClassName ? `saltCheckbox-input ${inputClassName}` : "saltCheckbox-input"}
        data-indeterminate={indeterminate}
        disabled={disabled}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        readOnly={readOnly}
        ref={setInputRef}
        type="checkbox"
        value={value}
        {...restInputProps}
      />
      <CheckboxIcon
        checked={checked}
        error={error}
        indeterminate={indeterminate}
        readOnly={readOnly}
        validationStatus={validationStatus}
      />
      {/*
        The label is a BARE child, not wrapped in a span — deliberately unlike Switch below.
        Wrapping it added a flex child the stylesheet does not style and shifted the spacing.
        Caught by the browser A/B, which is the only place a stray element like this shows up.
      */}
      {label}
    </label>
  );
});

export interface SwitchProps
  extends Omit<
    React.InputHTMLAttributes<HTMLLabelElement>,
    "onChange" | "onBlur" | "onFocus" | "value" | "defaultChecked" | "checked" | "children"
  > {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>> & DataAttributes;
  label?: React.ReactNode;
  name?: string;
  readOnly?: boolean;
  value?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

export const Switch = React.forwardRef<HTMLLabelElement, SwitchProps>(function Switch(
  {
    checked: checkedProp,
    className,
    defaultChecked,
    disabled: disabledProp,
    inputProps = {},
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    readOnly: readOnlyProp,
    value,
    ...rest
  },
  ref,
) {
  const {
    "aria-describedby": inputDescribedBy,
    "aria-labelledby": inputLabelledBy,
    className: inputClassName,
    onChange: inputOnChange,
    ...restInputProps
  } = inputProps;

  const [checked, setChecked] = useControlled<boolean>({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
  });

  const {
    a11yProps: formFieldA11yProps,
    disabled: formFieldDisabled,
    readOnly: formFieldReadOnly,
  } = useFormFieldProps();

  const disabled = formFieldDisabled || disabledProp;
  const readOnly = formFieldReadOnly || readOnlyProp;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.nativeEvent.defaultPrevented || readOnly) return;
    setChecked(event.target.checked);
    onChange?.(event);
    inputOnChange?.(event);
  };

  const classes = ["saltSwitch"];
  if (disabled) classes.push("saltSwitch-disabled");
  if (checked) classes.push("saltSwitch-checked");
  if (readOnly) classes.push("saltSwitch-readOnly");
  if (className) classes.push(className);

  return (
    <label className={classes.join(" ")} ref={ref} {...rest}>
      <input
        aria-describedby={join(formFieldA11yProps?.["aria-describedby"], inputDescribedBy)}
        aria-labelledby={join(formFieldA11yProps?.["aria-labelledby"], inputLabelledBy)}
        aria-readonly={readOnly || undefined}
        checked={checked}
        className={inputClassName ? `saltSwitch-input ${inputClassName}` : "saltSwitch-input"}
        defaultChecked={defaultChecked}
        disabled={disabled}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        // A checkbox that announces "on/off" rather than "checked/unchecked".
        role="switch"
        type="checkbox"
        value={value}
        {...restInputProps}
      />
      <span className="saltSwitch-track">
        <span className="saltSwitch-thumb">
          {/* Solid tick when operable, outline tick when read-only — the shape carries the
              state for anyone who cannot rely on the track's colour alone. */}
          {/*
            No `saltSwitch-icon` class. The vendor emits one, but NOTHING defines it — not
            salt-core.css, not the component's own injected stylesheet. It is dead vocabulary,
            our dead-class guard flagged it the moment it was copied across, and carrying it
            forward is exactly what that guard exists to stop. This is a deliberate deviation
            from the vendor's DOM with no visual consequence, because the class styled nothing.
          */}
          {checked && !readOnly ? <CheckmarkSolidIcon aria-hidden /> : null}
          {checked && readOnly ? <CheckmarkIcon aria-hidden /> : null}
        </span>
      </span>
      {label ? <span className="saltSwitch-label">{label}</span> : null}
    </label>
  );
});
